import React, { useCallback, useContext, useMemo } from "react";
import {
  AirDisruptionHtsMerchandising,
  HtsMobileContinueButton,
  useDeviceTypes,
} from "halifax";
import { useDispatch, useSelector } from "react-redux";
import {
  DelayId,
  DelayOffer,
  MissedConnectionId,
  MissedConnectionOffer,
} from "redmond";
import {
  setSelectedDisruptionProtectionId,
  setSelectedHtsDisruptionOfferId,
} from "../../../actions/actions";
import {
  DO_NOT_APPLY_OPTION_KEY,
  selectedHtsDisruptionOfferIdSelector,
} from "../../../reducer";
import { ClientContext } from "../../../../../App";
import { getSelectedAccountReferenceId } from "../../../../rewards/reducer";

enum DisruptionProtectionOption {
  No,
  Yes,
}

export const Cap1AirDisruptionHtsMerchandising = (props: {
  disruptionOffer: DelayOffer | MissedConnectionOffer;
  onLoadSuccess?: () => void;
  onLoadError?: () => void;
  isLoading: boolean;
  isError: boolean;
  onContinue?: () => void;
  selectedDisruptionProtectionId: MissedConnectionId | DelayId | null;
  retryCount: number;
}) => {
  const dispatch = useDispatch();

  const selectedHtsDisruptionOfferId = useSelector(
    selectedHtsDisruptionOfferIdSelector
  );

  const {
    disruptionOffer,
    onLoadSuccess,
    onLoadError,
    isLoading,
    isError,
    onContinue,
    selectedDisruptionProtectionId,
    retryCount,
  } = props;

  const { isAgentPortal } = useContext(ClientContext);
  const { matchesMobile } = useDeviceTypes();
  const selectedAccountReferenceId = useSelector(getSelectedAccountReferenceId);

  let selectedValue: DisruptionProtectionOption | undefined;

  if (isAgentPortal) {
    selectedValue = DisruptionProtectionOption.No;
  } else if (selectedDisruptionProtectionId !== null) {
    if (selectedDisruptionProtectionId.policyId !== DO_NOT_APPLY_OPTION_KEY) {
      selectedValue = DisruptionProtectionOption.Yes;
    } else {
      selectedValue = DisruptionProtectionOption.No;
    }
  }

  const isSelected = selectedValue !== undefined;
  const isMobileContinueButtonVisible =
    matchesMobile && !isLoading && (isSelected || isError);

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    }
  };

  const selectNoOffer = useCallback(
    (unselectedHtsDisruptionOfferIdValue?: string) => {
      dispatch(
        setSelectedDisruptionProtectionId({
          policyId: DO_NOT_APPLY_OPTION_KEY,
          productId: DO_NOT_APPLY_OPTION_KEY,
          quoteId: undefined,
        })
      );
      if (unselectedHtsDisruptionOfferIdValue) {
        dispatch(
          setSelectedHtsDisruptionOfferId(unselectedHtsDisruptionOfferIdValue)
        );
      }
    },
    [dispatch]
  );

  const { rewardsType, rewardsConversion } = useMemo(() => {
    if (!selectedAccountReferenceId) {
      return { rewardsType: undefined, rewardsConversion: undefined };
    }

    const rewardsPremiumAmount =
      disruptionOffer.premiumPricing.totalPrices.rewards[
        selectedAccountReferenceId
      ];

    if (
      !rewardsPremiumAmount ||
      rewardsPremiumAmount.value === 0 ||
      disruptionOffer.premiumPricing.totalPrices.fiat.value === 0
    ) {
      return { rewardsType: undefined, rewardsConversion: undefined };
    }

    return {
      rewardsType: rewardsPremiumAmount.currency.toLowerCase(),
      rewardsConversion:
        rewardsPremiumAmount.value /
        disruptionOffer.premiumPricing.totalPrices.fiat.value,
    };
  }, [disruptionOffer, selectedAccountReferenceId]);

  const { merchandisingUrl: merchandisingUrlString } = disruptionOffer;

  const merchandisingUrl = useMemo(() => {
    if (!merchandisingUrlString) {
      return null;
    }

    const url = new URL(merchandisingUrlString);

    if (rewardsType && rewardsConversion) {
      url.searchParams.set("rewardsType", rewardsType);
      url.searchParams.set("rewardsConversion", rewardsConversion.toString());
    }

    if (retryCount > 0) {
      url.searchParams.set("retryCount", retryCount.toString());
    }

    return url;
  }, [merchandisingUrlString, rewardsType, rewardsConversion, retryCount]);

  const handleLoadError = useCallback(() => {
    selectNoOffer();
    if (onLoadError) {
      onLoadError();
    }
  }, [selectNoOffer, onLoadError]);

  if (!merchandisingUrl) {
    return null;
  }

  return (
    <>
      <AirDisruptionHtsMerchandising
        merchandisingUrl={merchandisingUrl}
        onSelect={(eventData) => {
          if (eventData.attached && !isAgentPortal) {
            const id = {
              ...disruptionOffer?.id,
            };
            dispatch(setSelectedDisruptionProtectionId(id));
            dispatch(setSelectedHtsDisruptionOfferId(eventData.offer.offerId));
          } else {
            selectNoOffer(eventData.selected);
          }
        }}
        onLoadSuccess={onLoadSuccess}
        onLoadError={handleLoadError}
        isLoading={isLoading}
        isError={isError}
        offerId={disruptionOffer.id.productId}
        selected={selectedHtsDisruptionOfferId}
      />
      {isMobileContinueButtonVisible && (
        <HtsMobileContinueButton
          onClick={handleContinue}
          dataTestId="flights-disruption-continue-button"
        />
      )}
    </>
  );
};
